import React, {Suspense, useState, useEffect, useRef} from "react";
import {useParams} from "react-router-dom";
import EformApiClient from "../api/EformsApi";
import FormDynamic from "../containers/FormDynamic";
import { useAuthenticationContext } from '../context/AuthenticationContext'
import { mapSlugToStaticUserData } from '../utils/mapFormUserData'
import {
  getObjectPart,
  getWidgetPath,
  fillInFileValues,
  loadFiles,
  getFileIDsFromData,
  initValueObjects,
  scopeToID
} from "../utils/utils";
import {
  ID_DELIMITER,
  SCOPE_DELIMITER,
  CUSTOM_FORMS_FOLDER
} from "../constants";
import FileUpload from "../containers/FileUpload";
import Submit from "../containers/Submit";
import LoaderOverlay from "../components/_common/LoaderOverlay";
import {AddFiles, CancelFile} from "../interfaces/FileUpload";
import SubmitType from "../interfaces/Submit";
import Uploads from "../interfaces/Uploads";
import * as qs from 'qs';
import ErrorScreen from "../containers/ErrorScreen";
import ModalBox from "../components/ModalBox";
import { initFormLocale, translate } from "../custom/utils/translateForm";
import i18n from "../i18n";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import SuperContext from "../context/SuperContext";

const FORM_WITH_TRANSLATE = [
  "specificky-lecebny-program",
  "hlaseni-o-zahajeni-preruseni-obnoveni-a-ukonceni-uvadeni-leciveho-pripravku-na-trh",
  "zadost-o-umozneni-uvedeni-cizojazycne-sarze-leciveho-pripravku-do-obehu",
  "zadost-o-souhlas-s-dovozem-leciveho-pripravku-ze-treti-zeme"
]
enum FormErrorEnum {
  notSpec = 'submit.err.notSpec',
  cantRead = 'submit.err.cantRead',
}
const organization = {
  "id": "3ffaedc4-cccb-4658-af2a-aa0f141c4a23",
  "name": "Státní ústav pro kontrolu léčiv",
  "contacts": null,
  "addresses": null,
  "attributes": null,
  "description": null,
  "identifications": null
}

function mergeDeepObject(defaultData: any, parts: any, val: any): any {
  const thisKey = parts[0]
  const otherKeys = parts.slice(1)
  if (!thisKey) {
    return mergeDeepObject(defaultData, otherKeys, val);
  }
  if (parts.length < 2) {
    return {...defaultData, [thisKey]: val}
  } else {
    return {...defaultData, [thisKey]: mergeDeepObject(defaultData[thisKey] || {}, otherKeys, val)}
  }
}

const Form: React.FC = () => {
  const [publicID, setPublicID] = useState('');
  const [variableID, setVariableID] = useState('');
  const [prevID, setPrevID] = useState<any>(undefined);
  const [prevFormData, setPrevFormData] = useState<any>(undefined);
  const [specs, setSpecs] = useState<any>();
  const [values, setValues] = useState<any>();
  const [CustomFormRenderer, setCustomFormRenderer] = useState<any>();
  const [isLoading, setLoading] = useState(true);
  const [isError, setError] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [isFormDisabled, setFormDisabled] = useState(false);
  const [isOpenModal, setOpenModal] = useState(false);
  const [isModalBtn, setModalBtn] = useState(false);
  const [againVals, setAgainVals] = useState<any>({});
  const [resData, setResData] = useState<any>({});
  const [submission, setSubmission] = useState<any>();
  const {formSlug, organizationSlug, submissionID, locale} = useParams() as any;
  const [submSlug, setSubmSlug] = useState('')
  const formRef: any = useRef();

  const { isAuthenticated, userData, loadingAuth } = useAuthenticationContext()
  const [applyUserDataToSlug, setApplyUserDataToSlug] = useState('')

  useEffect(() => {
    if (isAuthenticated && userData && applyUserDataToSlug !== formSlug && !submissionID) {

      const defaultUserData = {
        ...mapSlugToStaticUserData(userData)[formSlug]
      }
      setValues(defaultUserData)

      setApplyUserDataToSlug(formSlug)
    }
    return () => {
      setApplyUserDataToSlug('')
    }
  }, [isAuthenticated, formSlug]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    if(locale && locale !== i18n.language){
      i18n.changeLanguage(locale)
    }
  },[locale])

  useEffect(() => {
    const createChildPrefixedID = (component: any,
                                   componentID: string,
                                   componentChild: any,
                                   counter: number) =>
      component.ui.widget === "Row"
        ? createPrefixedID(
        componentID,
        "Col",
        createPrefixedID(
          counter.toString(),
          componentChild.ui.widget,
          counter.toString()
        )
        )
        : createPrefixedID(
        componentID,
        componentChild.ui.widget,
        counter.toString()
        );

    const parseComponentLevel = (spec: any,
                                 component: any,
                                 componentID: string,
                                 scope: string | undefined) => {
      let layout: any = {
        id: componentID
      };
      let components: any = {
        [componentID]: {
          ...component,
          ...getObjectPart(spec, componentID.split(SCOPE_DELIMITER)),
          properties: undefined //remove useless "properties" prop coming from specs
        }
      };
      let counter = 0;

      if (!component.components) {
        return {components, layout};
      }
      components[componentID] = {
        ...components[componentID],
        components: []
      };
      layout.children = [];
      component.components.forEach((childComponent: any) => {
        const childID =
          childComponent.id ||
          parseScope(childComponent.scope, scope) ||
          createChildPrefixedID(
            component,
            componentID,
            childComponent,
            counter
          );
        components[componentID].components.push(childID);
        const level = parseComponentLevel(
          spec,
          childComponent,
          childID,
          parseScope(childComponent.scope, scope) || scope
        );
        components = {
          ...components,
          ...level.components
        };
        layout.children = layout.children.concat(level.layout);
        counter++;
      });
      return {components, layout};
    };

    const createLayoutComponents = (components: any, layout: any) => {
      if (!layout) return {};
      let allComponents = {...components};
      let allLayout: any = [];
      layout.forEach((item: any) => {
        const component = components[item.id];
        let newChildren: any;
        if (component.ui && component.ui.widget === "Row") {
          let counter = 0;
          newChildren = item.children.map((child: any) => {
            const colID = createPrefixedID(item.id, "Col", counter.toString());
            const childComponent = components[child.id];
            allComponents = {
              ...allComponents,
              [colID]: createColumnComponent(
                colID,
                childComponent.ui.attributes?.size
              )
            };
            counter++;
            return {id: colID, children: [child]};
          });
        } else {
          newChildren = item.children;
        }
        const childrenLayoutComponents = createLayoutComponents(
          allComponents,
          newChildren
        );
        allLayout.push({
          ...item,
          children: childrenLayoutComponents.layout
        });
        allComponents = {
          ...allComponents,
          ...childrenLayoutComponents.components
        };
      });
      return {components: allComponents, layout: allLayout};
    };

    const parseComponents = (schema: any) => {
      let components: any = {};
      let layout: any = [];
      let counter = 0;
      schema.layout?.forEach((item: any) => {
        const scope = parseScope(item.scope, "");
        const id =
          item.id ||
          scope ||
          createPrefixedID(item.ui.widget, "", counter.toString());
        const level = parseComponentLevel(schema, item, id, scope);
        components = {
          ...components,
          ...level.components
        };
        layout = layout.concat(level.layout);
        counter++;
      });
      return {components, layout};
    };


    const setDynamicFormData = (spec: any, values: any) => {
      const components = parseComponents(spec.schema);
      const allComponents = createLayoutComponents(
        components.components,
        components.layout
      );
      setSpecs({
        ...spec,
        schema: {
          ...spec.schema,
          components: populateComponentsWithValues(
            allComponents.components,
            values
          ),
          layout: allComponents.layout
        }
      });
    };

    const setCustomFormData = (slug: string, spec: any) => {
      const path = `${CUSTOM_FORMS_FOLDER}/${slug}`;
      const Renderer = React.lazy(() => import(`../custom/${path}`)); //"custom" must be set here in the import statement for webpack, otherwise it will fail to import in build
      setCustomFormRenderer(Renderer);
      setSpecs(spec);
    };

    function setDefaultValsFromParams(params: any) {
      let defaultData = {};
      for (let key in params) {
        let val = params[key]
        let parts = key.trim().split('/')
        defaultData = mergeDeepObject(defaultData, parts, val);
      }
      setValues(defaultData);
    }

    async function processParamPrev() {
      let qparams: any = qs.parse(window.location.search.slice(1));
      let prevIDparam = 'prev' in qparams ? qparams['prev'] : undefined;
      if (prevIDparam) {
        try {

          const prevForm: any = await EformApiClient.submission.getSubmission(prevIDparam);
          const acl = await  EformApiClient.submission.getSubmissionAcl(prevIDparam);
          const prevEditable = acl.allowedSubmissionActions.indexOf('update') !== -1

          if (prevEditable) { // predchozi form musi byt editovatelny = nezamknuty
            setPrevID(prevIDparam);
            setPrevFormData(prevForm.data);
          }
          if ('variableId' in prevForm) {
            setVariableID(prevForm.variableId);
          }

        } catch (e) {
          console.log('Err v nacteny prev')
        }

      }
    }

    async function fetchSpecs(formSlug: string, organizationSlug: string) {
      try {
        await initFormLocale(formSlug)
        const formData = await EformApiClient.forms.getFormBySlug(organizationSlug, formSlug);
        const schemaData = await EformApiClient.formSchema.getFormSchemaByIri(formData.formSchema);
        const specData:any = await EformApiClient.formSchema.getFormSchemaVersionByIri(schemaData.currentVersion);
        specData.idFull = schemaData.currentVersion;

        await processParamPrev();
        let qparams: any = qs.parse(window.location.search.slice(1));
        if ('d' in qparams) {
          setDefaultValsFromParams(qparams['d']);
        }

        if (isCustomSpec(specData))
          setCustomFormData(specData.layout.widget, specData);
        else setDynamicFormData(specData, {});
      } catch (e) {
        console.error(e);
        setErrorText(FormErrorEnum.notSpec)
        setError(true);
      } finally {
        setLoading(false);
      }
    }

    async function fetchSubmission(submissionID: string) {
      try {

        let editable = false;
        try {
          const acl = await  EformApiClient.submission.getSubmissionAcl(submissionID);
          editable = acl.allowedSubmissionActions.indexOf('update') !== -1
          setPublicID(submissionID);
          const read = acl.allowedSubmissionActions.indexOf('read') !== -1
          if (!read) {
            setErrorText(FormErrorEnum.cantRead)
            setError(true);
            return
          }
        } catch (e) {
          console.log('no ACL route ACL');
        }
        let qparams: any = qs.parse(window.location.search.slice(1));
        if (!editable || 'printable' in qparams) {
          setFormDisabled(true); //submitted form cannot be edited
        }
        const submissionData: any = await EformApiClient.submission.getSubmission(submissionID);
        setSubmission(submissionData);
        if ('npsPrev' in submissionData.data) {
          const prevIDparam = submissionData.data.npsPrev;
          try {
            const prevForm: any = await EformApiClient.submission.getSubmission(prevIDparam);
            setPrevID(prevIDparam);
            setPrevFormData(prevForm.data);
          } catch (e) {
            console.log('Err v nacteny prev')
          }
        }

        const specData: any = await EformApiClient.formSchema.getFormSchemaVersionByIri(submissionData.formSchemaVersion);
        await initFormLocale(specData.layout.widget)
        setSubmSlug(specData.layout.widget)
        let submissionValues;
        try {
          const fileIDs = getFileIDsFromData(
            submissionData.data,
            specData.schema.properties
          );
          const files = await loadFiles(fileIDs);
          submissionValues = await fillInFileValues(
            submissionData.data,
            specData.schema.properties,
            files
          );
        } catch (e) {
          console.error(e);
          submissionValues = undefined;
        }
        submissionValues = initValueObjects(
          submissionValues,
          specData.schema.properties
        );
        console.log('submit value', submissionValues)
        setValues(submissionValues);
        specData.idFull = submissionData.formSchemaVersion;

        if (isCustomSpec(specData))
          setCustomFormData(specData.layout.widget, specData);
        else setDynamicFormData(specData, submissionValues);
      } catch (e) {
        console.error(e);
        setErrorText(FormErrorEnum.notSpec)
        setError(true);
      } finally {
        setLoading(false);
      }
    }


    if (submissionID) {
      fetchSubmission(submissionID);
    } else if (formSlug && organizationSlug) {
      fetchSpecs(formSlug, organizationSlug);
    } else {
      setLoading(false);
      return;
    }
  }, [formSlug, organizationSlug, submissionID]); // eslint-disable-line react-hooks/exhaustive-deps

  const createPrefixedID = (prefix: string, mid: string, suffix: string) => {
    if (!mid) return `${prefix}${ID_DELIMITER}${suffix}`;
    return `${prefix}${ID_DELIMITER}${mid}${ID_DELIMITER}${suffix}`;
  };

  const createColumnComponent = (id: string, size: any) => ({
    id,
    ui: {
      attributes: {
        size
      },
      widget: "Col"
    }
  });

  const parseScope = (scope: string, parentScope?: string) => {
    if (!scope) return "";
    const out =
      scope[0] === "#"
        ? scope.slice(2, scope.length)
        : `${parentScope}${scope}`; //get rid of #/ at the beginning
    return out.replace(/\//g, SCOPE_DELIMITER);
  };

  const populateComponentsWithValues = (components: any, values: any) => {
    const componentsOut: any = {};
    Object.keys(components).forEach((id: string) => {
      componentsOut[id] = {
        ...components[id],
        value: values[id]
      };
    });
    return componentsOut;
  };

  const getChildrenComponents = (components: any, layoutItem: any) => {
    if (!Array.isArray(layoutItem.children)) {
      console.error(`Wrong children for "${layoutItem.id}".`);
      return;
    }
    return layoutItem.children.map((layoutItemChild: any) => {
      const id = scopeToID(layoutItemChild.id);
      if (layoutItemChild.children) {
        if (!components[id]) {
          console.error(`In layout found non-existing component "${id}".`);
          return id;
        }
        if (components[id].props.ui.widget !== "Variable")
          components[id].props.nested = true;
        components[id].props.components = getChildrenComponents(
          components,
          layoutItemChild
        );
      }
      return id;
    });
  };

  const loadComponents = (specs: any) => {
    const components: any = {};
    Object.entries(specs.components).forEach(([key, component]: any) => {
      if (!component.ui || !component.ui.widget) {
        console.error(`Missing widget for "${key}".`);
        return;
      }
      const isCustom = component.custom || component.customForm;
      const path = getWidgetPath(component.ui.widget, isCustom);
      let Renderer;
      if (isCustom) {
        Renderer = React.lazy(() => import(`../custom/${path}`)); //"custom" must be set here in the import statement for webpack, otherwise it will fail to import in build
      } else {
        Renderer = React.lazy(() => import(`../components/${path}`)); //"components" must be set here in the import statement for webpack, otherwise it will fail to import in build
      }
      const id = scopeToID(key);
      components[id] = createComponentObject(component, id, Renderer);
    });
    return components;
  };

  const createComponentObject = (component: any,
                                 id: string,
                                 Renderer: any) => ({
    Render: Renderer,
    props: {
      ...component,
      id,
      ui: {
        ...component.ui,
        attributes: {
          ...normalizeAttributesPropNames(component.ui.attributes)
        }
      }
    }
  });

  const normalizeAttributesPropNames = (attributes: any) => {
    if (!attributes) return {};
    return {
      ...attributes,
      // rename class and style props going from specs
      class: undefined,
      style: undefined,
      classNames: attributes?.class || undefined,
      styles: attributes?.style || undefined
    }
      ;
  };

  const setUpLayoutComponents = (specs: any, components: any) => {
    specs.layout.forEach((item: any) => {
      if (!item.children) return;
      const id = scopeToID(item.id);
      if (components[id].props.ui.widget !== "Variable")
        components[id].props.nested = true;
      components[id].props.components = getChildrenComponents(components, item);
    });
    return components;
  };

  const getComponents = (specs: any) => {
    if (!specs.layout) {
      console.error(`Layout property missing.`);
      return {};
    }
    const components = loadComponents(specs);
    const componentsWithLayout = setUpLayoutComponents(specs, components);
    return componentsWithLayout;
  };

  const isCustomSpec = (spec: any) => !!spec.layout.widget;


  const openModal = (noBtn?: boolean, againValues?: any, resData?: any) => {
    setOpenModal(true);
    setModalBtn(!noBtn)
    setAgainVals(againValues)
    setResData(resData)
  }

  const title = organization && specs ?  translate("title", specs?.layout?.form?.title) : 'title';
  const description = organization && specs ?  translate("description", specs?.layout?.form?.description) : 'desc';

  if (title !== "title"){
    document.title = title + " | Státní ústav pro kontrolu léčiv";
  }

  const slug = formSlug || submSlug
  const isTranslate = FORM_WITH_TRANSLATE.includes(slug) || slug?.startsWith('platby')

  return (
    <>
    <Header langSwith={isTranslate}/>
    <LoaderOverlay
      isLoading={isLoading || loadingAuth}
      fixed
      overflow={false}
      className="page-all"
    >
      <Suspense fallback={null}>
        <ModalBox
          isOpenModal={isOpenModal}
          isModalBtn={isModalBtn}
          againVals={againVals}
          formSlug={formSlug}
          showInvoice={!!againVals && !!resData}
          resData={resData}
          isEdit={!!publicID}
        />
      </Suspense>

      {isError && (
          <ErrorScreen errorText={i18n.t(errorText)}/>
      )}
      {specs && (
        <>
          {!loadingAuth &&
        <div className="page-content">
          <Suspense fallback={null}>
            <SuperContext.Provider
              value={{
                submission,
                formSchemaVersion:
                  specs.idFull.split('/').pop() || specs.idFull,
              }}
            >
            <FileUpload formRef={formRef}>
              {(handleAddFiles: AddFiles,
                handleCancelFile: CancelFile,
                uploads: Uploads) => (
                <Submit formRef={formRef} properties={specs.schema.properties}>
                  {(submit: SubmitType,
                    submitReset: () => void,
                    submitError: string,
                    submitSuccess: string,
                    submitLoading: boolean) => (
                    <>
                      {specs.schema.components && (
                        <FormDynamic
                          classNames={{
                            description: specs.schema.class?.description,
                            form: specs.schema.class?.form,
                            title: specs.schema.class?.title
                          }}
                          components={getComponents(specs.schema)}
                          description={description}
                          error={submitError}
                          isLoading={submitLoading}
                          layout={specs.layout}
                          onAddFiles={handleAddFiles}
                          onCancelFile={handleCancelFile}
                          onSubmit={submit}
                          onSubmitReset={submitReset}
                          organization={organization}
                          ref={formRef}
                          formSlug={slug}
                          schemaID={specs.idFull}
                          specID={specs.id}
                          specsValues={values}
                          success={submitSuccess}
                          title={title}
                          uploads={uploads}
                        />
                      )}
                      {!specs.schema.components && (
                        <CustomFormRenderer
                          description={description}
                          disabled={isFormDisabled}
                          error={submitError}
                          isLoading={submitLoading}
                          onAddFiles={handleAddFiles}
                          onCancelFile={handleCancelFile}
                          onSubmit={submit}
                          onSubmitReset={submitReset}
                          organization={organization}
                          ref={formRef}
                          publicID={publicID}
                          prevID={prevID}
                          variableID={variableID}
                          prevFormData={prevFormData}
                          formSlug={slug}
                          schemaID={specs.idFull}
                          schema={specs.schema}
                          success={submitSuccess}
                          title={title}
                          uploads={uploads}
                          values={values}
                          openModal={openModal}
                        />
                      )}
                    </>
                  )}
                </Submit>
              )}
            </FileUpload>
            </SuperContext.Provider>
          </Suspense>
        </div>
          }
        </>
      )}
    </LoaderOverlay>
      <Footer/>
    </>
  );
};

export default Form;
